/**
 * @license
 *-------------------------------------------------------------------------------------------
 * Copyright © 2024 Progress Software Corporation. All rights reserved.
 * Licensed under commercial license. See LICENSE.md in the package root for more information
 *-------------------------------------------------------------------------------------------
 */
"use client";
import * as t from "react";
import o from "prop-types";
import { validatePackage as Ce, useZIndexContext as ye, useId as V, useDir as Ie, dispatchEvent as b, getActiveElement as ve, Keys as c, classNames as D, kendoThemeMaps as Ee, ZIndexContext as xe, getTabIndex as De, IconWrap as Ne, svgIconPropType as we } from "@progress/kendo-react-common";
import { FloatingActionButtonItem as Ae } from "./FloatingActionButtonItem.mjs";
import { packageMetadata as Fe } from "../package-metadata.mjs";
import { position as Oe, getTextDirectionClass as Re, getAnchorAlign as ze, getPopupAlign as Me } from "./utils.mjs";
import { Popup as Pe } from "@progress/kendo-react-popup";
const Te = 2, Be = 100, Y = t.forwardRef((T, ee) => {
  Ce(Fe);
  const {
    align: u = h.align,
    alignOffset: B,
    className: $,
    disabled: d,
    icon: N,
    svgIcon: w,
    iconClass: K,
    id: S,
    items: n,
    item: te,
    text: C,
    positionMode: A = h.positionMode,
    size: y = h.size,
    style: oe,
    rounded: I = h.rounded,
    themeColor: F = h.themeColor,
    overlayStyle: ae,
    tabIndex: ne,
    accessKey: se,
    popupSettings: v = {},
    modal: le,
    onClick: O,
    onItemClick: W,
    onFocus: R,
    onBlur: Z,
    onKeyDown: _,
    onOpen: H,
    onClose: j,
    ...re
  } = T, L = ye(), X = L ? L + Te : Be, U = t.useRef(null), l = t.useRef(null), ie = t.useRef(null), ce = t.useRef(null), q = t.useCallback(
    () => {
      l.current && l.current.focus();
    },
    []
  ), f = t.useCallback(
    () => ({
      element: l.current,
      focus: q
    }),
    [q]
  );
  t.useImperativeHandle(U, f), t.useImperativeHandle(ee, () => U.current);
  const [r, i] = t.useState(!1), [E, p] = t.useState(!1), [g, s] = t.useState(-1), ue = V() + "-button-id", z = Ie(l, T.dir), k = z === "rtl", x = V() + "-list-id";
  t.useEffect(
    () => {
      Oe(l, u, B, k);
    },
    [l, u, B, k]
  ), t.useEffect(
    () => {
      E && l && l.current && l.current.focus();
    },
    [E, l]
  );
  const m = t.useCallback(
    (e, a) => {
      n && b(
        a ? H : j,
        e,
        f(),
        void 0
      );
    },
    [H, j, n]
  ), G = t.useCallback(
    (e) => {
      !e.target || d || (!n && O ? b(
        O,
        e,
        f(),
        void 0
      ) : (i(!r), p(!0), s(r ? -1 : 0), m(e, !r)));
    },
    [
      i,
      p,
      s,
      O,
      m,
      r,
      n,
      d
    ]
  ), de = t.useCallback(
    (e) => {
      p(!0), s(r ? 0 : -1), R && b(
        R,
        e,
        f(),
        void 0
      );
    },
    [R, p, s]
  ), fe = t.useCallback(
    (e) => {
      p(!1), i(!1), s(-1), b(
        Z,
        e,
        f(),
        void 0
      ), r && m(e, !1);
    },
    [Z, p, i, s, m]
  ), pe = t.useCallback(
    (e) => {
      e.preventDefault();
    },
    []
  ), M = t.useCallback(
    (e, a) => {
      n && (n[a].disabled || b(
        W,
        e,
        f(),
        {
          itemProps: n[a],
          itemIndex: a
        }
      ));
    },
    [W]
  ), me = t.useCallback(
    (e, a) => {
      !e.target || !n || (s(a), i(!1), M(e, a), m(e, !1));
    },
    [s, i, M, m]
  ), be = t.useCallback(
    (e) => {
      ve(document) === l.current && e.preventDefault();
    },
    [l]
  ), ge = t.useCallback(
    (e) => {
      const a = g, P = n ? n.length : -1;
      if (e.altKey) {
        !r && e.keyCode === c.down && (e.preventDefault(), i(!0), s(0)), r && e.keyCode === c.up && (e.preventDefault(), i(!1), s(-1));
        return;
      }
      switch (e.keyCode) {
        case c.enter:
        case c.space:
          a >= 0 && M(e, a), n || G(e), e.preventDefault(), i(!r), s(r ? -1 : 0);
          break;
        case c.esc:
          e.preventDefault(), i(!1), s(-1);
          break;
        case c.home:
          e.preventDefault(), s(0);
          break;
        case c.end:
          e.preventDefault(), s(P - 1);
          break;
        case c.down:
        case c.right:
          e.preventDefault(), s(a + 1 >= P ? 0 : a + 1);
          break;
        case c.up:
        case c.left:
          e.preventDefault(), s(a - 1 < 0 ? P - 1 : a - 1);
          break;
      }
      b(
        _,
        e,
        f(),
        void 0
      );
    },
    [_, g, s, k, i]
  ), ke = t.useMemo(
    () => D(
      "k-fab k-fab-solid",
      {
        "k-fab-sm": y === "small",
        "k-fab-md": y === "medium",
        "k-fab-lg": y === "large",
        "k-disabled": d,
        "k-pos-absolute": A === "absolute",
        "k-pos-fixed": A === "fixed",
        "k-focus": E,
        [`k-rounded-${Ee.roundedMap[I] || I}`]: I,
        [`k-fab-solid-${F}`]: F
      },
      `k-${u.vertical}-${u.horizontal}`,
      $
    ),
    [F, y, I, d, A, u, E, $]
  ), he = n && n.map((e, a) => /* @__PURE__ */ t.createElement(
    Ae,
    {
      ...e,
      key: a,
      index: a,
      id: `${x}-${a}`,
      disabled: d || e.disabled,
      focused: g === a,
      dataItem: e,
      item: te,
      className: D(
        e.className,
        Re(z || "ltr", u.horizontal)
      ),
      onClick: me,
      onDown: be
    }
  )), J = !!((N || w) && !C), Q = (l.current ? l.current.offsetWidth : 0) / 2 - 32 / 2;
  return /* @__PURE__ */ t.createElement(xe.Provider, { value: X }, /* @__PURE__ */ t.createElement(t.Fragment, null, /* @__PURE__ */ t.createElement(
    "button",
    {
      ref: l,
      id: S || ue,
      role: "button",
      type: "button",
      "aria-disabled": d,
      "aria-expanded": n ? r : void 0,
      "aria-haspopup": !!n,
      "aria-label": `${C || ""} floatingactionbutton`,
      "aria-owns": n ? x : void 0,
      "aria-activedescendant": g >= 0 && n ? `${x}-${g}` : void 0,
      tabIndex: De(ne, d),
      accessKey: se,
      dir: z,
      disabled: d,
      className: ke,
      style: oe,
      onClick: G,
      onMouseDown: pe,
      onFocus: de,
      onBlur: fe,
      onKeyDown: ge,
      ...re
    },
    N || w ? /* @__PURE__ */ t.createElement(Ne, { className: "k-fab-icon", name: N, icon: w }) : K ? /* @__PURE__ */ t.createElement("span", { role: "presentation", className: K }) : null,
    C && /* @__PURE__ */ t.createElement("span", { className: "k-fab-text" }, C)
  ), le && r && /* @__PURE__ */ t.createElement(
    "div",
    {
      className: "k-overlay",
      style: {
        zIndex: X,
        ...ae
      }
    }
  ), /* @__PURE__ */ t.createElement(
    Pe,
    {
      ref: ce,
      anchor: l.current,
      show: r,
      animate: v.animate,
      popupClass: D("k-popup-transparent k-fab-popup", v.popupClass),
      anchorAlign: v.anchorAlign || ze(u, k),
      popupAlign: v.popupAlign || Me(u, k),
      style: { boxShadow: "none" }
    },
    /* @__PURE__ */ t.createElement(
      "ul",
      {
        ref: ie,
        role: "menu",
        "aria-labelledby": S,
        id: x,
        className: D("k-fab-items", {
          "k-fab-items-bottom": u.vertical !== "bottom",
          "k-fab-items-top": u.vertical === "bottom"
        }),
        style: {
          paddingLeft: J ? Q : void 0,
          paddingRight: J ? Q : void 0
        }
      },
      he
    )
  )));
});
Y.propTypes = {
  className: o.string,
  style: o.object,
  id: o.string,
  dir: o.string,
  tabIndex: o.number,
  accessKey: o.string,
  disabled: o.bool,
  icon: o.string,
  svgIcon: we,
  iconClass: o.string,
  text: o.string,
  alignOffset: o.shape({
    x: o.oneOfType([o.number, o.string]),
    y: o.oneOfType([o.number, o.string])
  }),
  align: o.shape({
    vertical: o.oneOf(["top", "middle", "bottom"]),
    horizontal: o.oneOf(["start", "center", "end"])
  }),
  positionMode: o.oneOf(["absolute", "fixed"]),
  size: o.oneOf([null, "small", "medium", "large"]),
  rounded: o.oneOf([null, "small", "medium", "large", "full"]),
  themeColor: o.oneOf([
    null,
    "primary",
    "secondary",
    "tertiary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
    "inverse"
  ]),
  modal: o.bool,
  overlayStyle: o.object
};
const h = {
  align: { vertical: "bottom", horizontal: "end" },
  size: "medium",
  rounded: "full",
  themeColor: "primary",
  positionMode: "fixed"
};
Y.displayName = "KendoFloatingActionButton";
export {
  Y as FloatingActionButton
};
